import { Box, Button, Container, Grid, Paper, Typography } from '@mui/material';
import { get, isEqual } from 'lodash';
import { Check, Close, Create } from '@mui/icons-material';
import { ManagementTable } from '../../components/ManagementTable';
import { Link, useLocation } from 'react-router-dom';
import { LoadingIndicator } from '../../components/LoadingIndicator';
import * as React from 'react';
import { useCallback, useState } from 'react';
import { usePreviewPagesSlice } from './slice';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectChangeField,
  selectDeletePages,
  selectPreviewPages,
} from './slice/selectors';
import { useDebounce } from 'utils/useDebounce';
import { useQuery } from 'utils/query';
import { GridSortDirection } from '@mui/x-data-grid-pro';
import { FilterBar } from '../FilterBar';
import { grey } from '@mui/material/colors';
import { ActionsMenu } from '../../components/ActionsMenu';
import { useConfirm } from '../../components/ConfirmDialog';
import { selectAppSettings } from '../App/slice/selectors';
import { useHasChanged } from '../../../utils/usePrevious';
import useHandleApiResponse, {
  withReason,
} from '../../../utils/useHandleApiResponse';

export function List() {
  const confirm = useConfirm();
  const { actions } = usePreviewPagesSlice();
  const dispatch = useDispatch();
  const { search } = useLocation();
  const previewPages = useSelector(selectPreviewPages);
  const deletePages = useSelector(selectDeletePages);
  const changeField = useSelector(selectChangeField);
  const settings = useSelector(selectAppSettings);

  // The API says how many failed; without this the list just reloaded and
  // a failed delete looked like it had worked.
  useHandleApiResponse(deletePages, {
    errorMessage: withReason('Delete failed', deletePages.error?.message),
  });
  useHandleApiResponse(changeField, {
    errorMessage: withReason('Update failed', changeField.error?.message),
  });

  const [selectedRows, setSelectedRows] = useState<Array<number | string>>([]);

  const { query, setQuery } = useQuery();
  const [pageSize, setPageSize] = React.useState<number>(
    +(query.pageSize || 25),
  );
  const [page, setPage] = React.useState<number>(+(query.page || 1));
  const [sort] = React.useState<
    [field: string, direction?: GridSortDirection] | undefined
  >(query.sort || undefined);

  const getFilters = () => query.filters;
  const [filters, setFilters] = React.useState(getFilters());

  // See People/List for the rationale: these effects sync list state <-> URL
  // and trigger loads. setQuery (useQuery) + local getFilters/loadPreviewPages
  // are recreated each render; tried adding them / useCallback + memoizing
  // setQuery, both caused refetch/navigation loops. Each effect is
  // intentionally keyed to the values that should trigger it.
  React.useEffect(() => {
    if (!isEqual(getFilters(), filters)) setFilters(getFilters());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const debouncedFilters = useDebounce(filters, 400);

  const deletePagesChanged = useHasChanged(deletePages.loading);
  const changeFieldChanged = useHasChanged(changeField.loading);

  React.useEffect(() => {
    if (
      (deletePagesChanged && !deletePages.loading) ||
      (changeFieldChanged && !changeField.loading)
    ) {
      loadPreviewPages();
    }
  });

  const doAction = useCallback(
    action => {
      switch (action) {
        case 'delete':
          confirm({
            title: `Delete (${selectedRows.length} selected)`,
          })
            .then(() =>
              dispatch(
                actions.deletePreviewPageRequest(selectedRows as number[]),
              ),
            )
            .catch(() => {});
          break;
        case 'set_active':
          confirm({
            title: `Set ${selectedRows.length} selected rows to active`,
          })
            .then(() =>
              dispatch(
                actions.changeFieldRequest(
                  selectedRows.map(id => ({
                    field_name: 'active',
                    field_value: true,
                    id: id as number,
                  })),
                ),
              ),
            )
            .catch(() => {});
          break;
        case 'set_inactive':
          confirm({
            title: `Set ${selectedRows.length} selected rows to inactive`,
          })
            .then(() =>
              dispatch(
                actions.changeFieldRequest(
                  selectedRows.map(id => ({
                    field_name: 'active',
                    field_value: false,
                    id: id as number,
                  })),
                ),
              ),
            )
            .catch(() => {});
          break;
      }
    },
    [selectedRows, actions, confirm, dispatch],
  );
  React.useEffect(() => {
    setQuery('pageSize', pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  React.useEffect(() => {
    setQuery('page', page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  React.useEffect(() => {
    setQuery('filters', filters);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  React.useEffect(() => {
    setQuery('sort', sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  React.useEffect(() => {
    loadPreviewPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, debouncedFilters, page, pageSize]);

  const loadPreviewPages = () => {
    console.log(debouncedFilters, page, pageSize);
    dispatch(
      actions.loadPreviewPagesRequest({
        ...query.filters,
        'page[number]': Math.max(page, 1),
        'page[size]': pageSize,
        sort: sort,
      }),
    );
  };
  return (
    <Box sx={{ my: 4 }}>
      <Container maxWidth="xl">
        <Grid
          container
          spacing={2}
          alignItems={'center'}
          justifyContent={'flex-start'}
        >
          <Grid item>
            <Typography variant="h1" color={'primary'}>
              All preview pages
            </Typography>
          </Grid>
          <Grid item sx={{ marginLeft: 'auto' }}>
            <Button
              size={'large'}
              startIcon={<Create color={'secondary'} />}
              component={Link}
              to={`./new`}
            >
              Add new page
            </Button>
          </Grid>
        </Grid>
      </Container>

      <Container maxWidth="xl">
        <Paper sx={{ my: 2 }}>
          <Box sx={{ px: 2, py: 1, backgroundColor: grey.A200 }}>
            <FilterBar
              fields={[
                { name: 'searchTerm', props: { label: 'Search pages' } },
                {
                  name: 'select',
                  props: {
                    label: 'User created/updated',
                    fieldName: 'user_id',
                    options: [
                      { value: '', label: 'All users' },
                      ...settings.users.map(u => ({
                        value: `${u.id}`,
                        label: `${u.first_name} ${u.last_name}`,
                      })),
                    ],
                  },
                },
              ]}
            />
          </Box>
          <Box sx={{ p: 2 }}>
            <ActionsMenu
              label={`Bulk actions (${selectedRows.length} selected)`}
              doAction={doAction}
              disabled={!selectedRows.length}
              actions={[
                {
                  type: 'delete',
                  label: 'Delete permanently',
                },
                {
                  type: 'set_active',
                  label: 'Set active',
                },
                {
                  type: 'set_inactive',
                  label: 'Set inactive',
                },
              ]}
            />
          </Box>
          <ManagementTable
            enableCheckboxSelection
            onSelectionChange={ids => setSelectedRows(ids)}
            defaultRowsSelected={selectedRows}
            rows={previewPages.data as any}
            columns={[
              {
                value: 'reference',
                label: 'Reference',
              },
              {
                value: 'expires_at',
                label: 'Expiry',
              },
              {
                value: v =>
                  `${new Date(v.created_at).toLocaleString()} by ${
                    v.created_by.first_name
                  } ${v.created_by.last_name} `,
                label: 'Created',
              },
              {
                value: v =>
                  `${new Date(v.updated_at).toLocaleString()} by ${
                    v.last_updated_by.first_name
                  } ${v.last_updated_by.last_name} `,
                label: 'Last updated',
              },
              {
                value: v =>
                  v.active ? (
                    <Check color={'success'} />
                  ) : (
                    <Close color={'error'} />
                  ),
                label: 'Status',
              },
              {
                classes: 'align-right',
                value: v => {
                  return (
                    <Button
                      sx={{ my: 1 }}
                      component={Link}
                      to={`./${v.id}`}
                      variant={'contained'}
                      color={'primary'}
                    >
                      Edit
                    </Button>
                  );
                },
              },
            ]}
            onClick={() => {}}
            page={page - 1}
            onChangePage={page => {
              setPage(page);
            }}
            onChangeRowsPerPage={size => setPageSize(size)}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[10, 25, 50]}
            count={get(previewPages, 'meta.total', 0)}
            loading={previewPages.loading}
            loadingComponent={
              <LoadingIndicator minHeight={300} message={'Loading data'} />
            }
          />
        </Paper>
      </Container>
    </Box>
  );
}
